import React, { useState } from 'react';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';
import TCPACheckbox from './TCPACheckbox';
import { TradeAppraisalFormData } from '../types';

interface TradeAppraisalFormProps {
  onSubmit: (data: TradeAppraisalFormData) => Promise<void>;
  isLoading: boolean;
  dealerName: string;
  initialData?: Partial<TradeAppraisalFormData>;
}

const TradeAppraisalForm: React.FC<TradeAppraisalFormProps> = ({
  onSubmit,
  isLoading,
  dealerName,
  initialData = {},
}) => {
  const [formData, setFormData] = useState<Partial<TradeAppraisalFormData>>({
    firstName: '',
    lastName: '',
    phone: '',
    year: '',
    make: '',
    model: '',
    mileage: '',
    condition: '',
    notes: '',
    tcpaConsent: false,
    ...initialData,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    if (!formData.year?.trim()) {
      newErrors.year = 'Year is required';
    }
    if (!formData.make?.trim()) {
      newErrors.make = 'Make is required';
    }
    if (!formData.model?.trim()) {
      newErrors.model = 'Model is required';
    }
    if (!formData.mileage?.trim()) {
      newErrors.mileage = 'Mileage is required';
    }
    if (!formData.tcpaConsent) {
      newErrors.tcpaConsent = 'You must agree to receive messages';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validate()) {
      await onSubmit(formData as TradeAppraisalFormData);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        overflowY: 'auto',
        flex: 1,
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1',
          borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: 'linear-gradient(to bottom right, #E1F7F4, #6A8AF7)',
          borderRadius: '8px',
        },
      }}
    >
      <Typography variant="h6" fontWeight={600} gutterBottom>
        Value Your Trade
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We'll pass this to our appraisal team so they can text you the estimate once it's ready.
      </Typography>

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
        Your Vehicle
      </Typography>

      <TextField
        fullWidth
        label="Year"
        value={formData.year}
        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
        error={!!errors.year}
        helperText={errors.year}
        disabled={isLoading}
        placeholder="2020"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Make"
        value={formData.make}
        onChange={(e) => setFormData({ ...formData, make: e.target.value })}
        error={!!errors.make}
        helperText={errors.make}
        disabled={isLoading}
        placeholder="Honda"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Model"
        value={formData.model}
        onChange={(e) => setFormData({ ...formData, model: e.target.value })}
        error={!!errors.model}
        helperText={errors.model}
        disabled={isLoading}
        placeholder="CR-V"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Mileage"
        value={formData.mileage}
        onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
        error={!!errors.mileage}
        helperText={errors.mileage}
        disabled={isLoading}
        placeholder="50,000"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Condition (Optional)"
        value={formData.condition}
        onChange={(e) => setFormData({ ...formData, condition: e.target.value })}
        disabled={isLoading}
        placeholder="Excellent, Good, Fair"
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Additional Notes (Optional)"
        value={formData.notes}
        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        disabled={isLoading}
        multiline
        rows={2}
        placeholder="Any additional details..."
        sx={{ mb: 3 }}
      />

      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
        Your Contact Information
      </Typography>

      <TextField
        fullWidth
        label="First name"
        value={formData.firstName}
        onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
        error={!!errors.firstName}
        helperText={errors.firstName}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Last name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        error={!!errors.lastName}
        helperText={errors.lastName}
        disabled={isLoading}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        type="tel"
        label="Phone number"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
        error={!!errors.phone}
        helperText={errors.phone}
        disabled={isLoading}
        sx={{ mb: 3 }}
      />

      <TCPACheckbox
        checked={formData.tcpaConsent || false}
        onChange={(checked) => setFormData({ ...formData, tcpaConsent: checked })}
        dealerName={dealerName}
        error={!!errors.tcpaConsent}
        disabled={isLoading}
      />

      <Button
        fullWidth
        variant="contained"
        onClick={handleSubmit}
        disabled={isLoading}
        sx={{
          mt: 3,
          py: 1.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        }}
      >
        {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Get My Trade Value'}
      </Button>
    </Box>
  );
};

export default TradeAppraisalForm;
